import type { RefObject } from 'react'
import { ListTodo, RefreshCw, XCircle } from 'lucide-react'
import clsx from 'clsx'
import type { ActivePin, ActivePinKind } from '../external-types'
import type { Integration, ActionItem } from '../external-types'
import { IntegrationsDropdown } from './IntegrationsDropdown'
import { TestOpsCommandLogo } from './TestOpsCommandLogo'

export interface ChatHeaderProps {
  selectedPortfolioId: string
  portfolios: Array<{ id: string; name: string; description?: string | null }>
  activePins: ActivePin[]
  clearingPinKey: string | null
  onClearPin: (kind: ActivePinKind, connectionId: string) => void
  onToggleMyActions: () => void
  actionItems: ActionItem[]
  integrations: Integration[]
  showIntegrations: boolean
  onToggleIntegrations: () => void
  onRefreshIntegrations: () => void
  integrationsButtonRef: RefObject<HTMLDivElement | null>
}

export function ChatHeader({
  selectedPortfolioId: _selectedPortfolioId,
  portfolios: _portfolios,
  activePins,
  clearingPinKey,
  onClearPin,
  onToggleMyActions,
  actionItems,
  integrations,
  showIntegrations,
  onToggleIntegrations,
  onRefreshIntegrations,
  integrationsButtonRef,
}: ChatHeaderProps) {
  const pendingCount = actionItems.filter((item) => item.status === 'pending').length

  return (
    <div className="bg-card">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-soft ring-1 ring-accent/30">
              <TestOpsCommandLogo size={28} />
            </div>
            <div>
              <h1 className="font-mono text-base font-bold tracking-tight text-foreground">TestOps Command</h1>
              <p className="text-xs text-muted-foreground font-mono">// Orchestrate your entire test stack.</p>
            </div>
          </div>

          {/* Pinned resources strip */}
          {activePins.length > 0 && (
            <div className="ml-auto mr-3 hidden max-w-[42%] items-center gap-2 overflow-x-auto md:flex">
              <span className="shrink-0 text-[0.7rem] uppercase tracking-wide text-muted-foreground">
                Working on
              </span>
              {activePins.map((pin) => {
                const key = `${pin.kind}:${pin.connection_id}`
                const clearing = clearingPinKey === key
                return (
                  <span
                    key={key}
                    className={clsx(
                      'group inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                      pin.stale
                        ? 'border-warning/30 bg-warning-soft text-warning-foreground'
                        : 'border-subtle bg-foreground/[0.04] text-foreground',
                    )}
                    title={
                      pin.stale
                        ? `Pinned ${new Date(pin.last_used_at).toLocaleDateString()} — confirm or clear`
                        : `Pinned ${pin.kind} (${pin.source})`
                    }
                  >
                    <span className="font-medium">{pin.display_name}</span>
                    <span className="text-[0.65rem] text-subtle-foreground">
                      {pin.kind === 'repository'
                        ? 'repo'
                        : pin.kind === 'project'
                          ? 'project'
                          : 'space'}
                    </span>
                    <button
                      type="button"
                      onClick={() => onClearPin(pin.kind, pin.connection_id)}
                      disabled={clearing}
                      className="ml-0.5 rounded-full p-0.5 text-subtle-foreground transition hover:bg-foreground/[0.10] hover:text-foreground disabled:text-disabled-foreground disabled:cursor-not-allowed"
                      aria-label={`Clear ${pin.kind} pin`}
                    >
                      {clearing ? (
                        <RefreshCw className="h-3 w-3 animate-spin" />
                      ) : (
                        <XCircle className="h-3 w-3" />
                      )}
                    </button>
                  </span>
                )
              })}
            </div>
          )}

          <div className="flex items-center gap-1.5">
            <button
              onClick={onToggleMyActions}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border bg-card px-2.5 text-xs font-medium text-foreground/80 transition hover:bg-foreground/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ListTodo className="h-3.5 w-3.5 text-muted-foreground" />
              <span>My Actions</span>
              {pendingCount > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-white">
                  {pendingCount}
                </span>
              )}
            </button>
            <IntegrationsDropdown
              integrations={integrations}
              show={showIntegrations}
              onToggle={onToggleIntegrations}
              onRefresh={onRefreshIntegrations}
              buttonRef={integrationsButtonRef}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
